import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {
    AlertTriangle,
    Archive,
    Check,
    ChevronDown,
    ChevronLeft,
    ChevronRight,
    ChevronUp,
    MessageSquare,
    Search,
    X
} from 'lucide-react';
import {useMsal} from "@azure/msal-react";
import ThreadModal from "./ThreadModal";
import TeamStatsModal from "./TeamStatsModal";

const API = 'https://api.gabrielny.com:8080/ai_zohoProxy.php';

const MARKETING_OPTIONS = ['Bridal Embed', 'Bridal Products In The Sort', 'Bridal Web Banners', 'Campaign & Brand Update Presentation', 'Clienteling & Technology Enablement', 'Display Co-Op', 'Email Marketing', 'Experience Ads / Social Co-Op', 'Fashion Book', 'Fashion Embed', 'Fashion Products in the Sort', 'Fashion Web Banners', 'G4 Request', 'Gabriel TV', 'General Marketing Review (Branding Check-In)', 'High Res Image Request', 'In Store Marketing', 'IT / Technical Issue', 'Marketing Program Review & Enrollment', 'Other Digital Co-Op', 'Other Physical Co-Op', 'Physical Billboard Co-Op', 'PPC Co-Op', 'Rewards', 'ShopNShop', 'Social Media Posting', 'Spring Gift Guide', 'Trunk Shows'];

function extractEmail(data) {
    if (Array.isArray(data) && data.length > 0) {
        const user = data[0];

        if (user.idTokenClaims && user.idTokenClaims.email) {
            return user.idTokenClaims.email;
        }
    }

    return null;
}


const ZohoTicketManager = ({ userEmail }) => {
    const [allTickets, setAllTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [c0Filter, setC0Filter] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: 'Scheduled_Date', direction: 'desc' });
    const [currentPage, setCurrentPage] = useState(1);
    const [entriesPerPage, setEntriesPerPage] = useState(10);
    const [archivedSearchTerm, setArchivedSearchTerm] = useState('');
    const [archivedPage, setArchivedPage] = useState(1);
    const [showArchived, setShowArchived] = useState(false);
    const [modalThread, setModalThread] = useState(null);
    const [editingAccount, setEditingAccount] = useState(null);
    const [editingMarketing, setEditingMarketing] = useState(null);
    const [statsMode, setStatsMode] = useState('all');
    const {accounts} = useMsal();
    const [activeBreakdown, setActiveBreakdown] = useState(null);
    const [showTeamStats, setShowTeamStats] = useState(false);
    const TEAM_STATS_ALLOWED = ['jchin@gabrielny.com', 'rodi.chamii@gabrielny.com'];
    let email = extractEmail(accounts);


    const statsTickets = useMemo(() => {
        return allTickets.filter(t => {
            const status = (t.Status || '').toLowerCase();

            if (statsMode === 'pending') return status === 'pending';
            if (statsMode === 'inserted') return status === 'approved' || status === 'rejected';
            return true;
        });
    }, [allTickets, statsMode]);

    const marketingStats = useMemo(() => {
        const map = {};
        let total = 0;

        statsTickets.forEach(t => {
            let marketing = t.Marketing;

            if (!marketing) return;

            try {
                if (typeof marketing === 'string') {
                    marketing = JSON.parse(marketing);
                }
            } catch {
                marketing = marketing.split(',').map(s => s.trim());
            }

            if (!Array.isArray(marketing)) return;

            marketing.forEach(m => {
                map[m] = (map[m] || 0) + 1;
                total++;
            });
        });

        const result = Object.entries(map)
            .map(([key, count]) => ({
                key,
                count,
                percent: total ? ((count / total) * 100).toFixed(1) : 0
            }))
            .sort((a, b) => b.count - a.count);

        return result;
    }, [statsTickets]);


    const retailerBreakdown = useMemo(() => {
        const map = {};
        const total = statsTickets.length;

        statsTickets.forEach(t => {
            const acct = t.Account_No?.trim();
            if (!acct) return;

            if (!map[acct]) {
                map[acct] = {
                    account: acct,
                    total: 0,
                    pending: 0,
                    inserted: 0
                };
            }

            map[acct].total++;

            const status = (t.Status || '').toLowerCase();
            if (status === 'pending') map[acct].pending++;
            if (status === 'approved' || status === 'rejected')
                map[acct].inserted++;
        });

        return Object.values(map)
            .map(r => ({
                ...r,
                percent: total ? ((r.total / total) * 100).toFixed(1) : 0
            }))
            .sort((a, b) => b.total - a.total);
    }, [statsTickets]);

    const retailerStats = useMemo(() => {
        const retailers = new Set();
        let withC0 = 0;

        statsTickets.forEach(t => {
            if (t.Account_No && t.Account_No.trim() !== '') {
                retailers.add(t.Account_No);
                withC0++;
            }
        });

        return {
            totalTickets: statsTickets.length,
            uniqueRetailers: retailers.size,
        };
    }, [statsTickets]);


    const StatCard = ({ title, value, subtitle, onClick, active }) => {
        return (
            <div
                onClick={onClick}
                className={`bg-white p-4 rounded-lg shadow-sm border cursor-pointer transition-all
                ${active ? 'border-blue-500 ring-2 ring-blue-100' : 'border-gray-200 hover:shadow-md hover:-translate-y-0.5'}
            `}
            >
                <div className="text-xs text-gray-500 uppercase tracking-wide">
                    {title}
                </div>
                <div className="text-2xl font-bold mt-1 text-gray-800">
                    {value}
                </div>
                {subtitle && (
                    <div className="text-xs text-gray-400 mt-1">
                        {subtitle}
                    </div>
                )}
            </div>
        );
    };



    const timelineStats = useMemo(() => {
        const days = 7;
        const map = {};
        const now = new Date();

        for (let i = 0; i < days; i++) {
            const d = new Date();
            d.setDate(now.getDate() - i);
            const key = d.toISOString().slice(0, 10);
            map[key] = 0;
        }

        statsTickets.forEach(t => {
            const rawDate = t.Scheduled_Date || t.Datetime;
            if (!rawDate) return;

            const date = new Date(rawDate);
            const key = date.toISOString().slice(0, 10);

            if (map.hasOwnProperty(key)) {
                map[key]++;
            }
        });

        const arr = Object.entries(map)
            .map(([date, count]) => ({
                date,
                count
            }))
            .sort((a, b) => new Date(a.date) - new Date(b.date));

        const max = Math.max(...arr.map(a => a.count), 1);

        return {
            data: arr,
            max
        };
    }, [statsTickets]);

    const fetchTickets = useCallback(async () => {
        try {
            const response = await fetch(`${API}?query=zoho_jobs_by_email&email=${email}`);
            let filtered = await response.json();
            console.log("filtered", filtered);
            setAllTickets(filtered);
            setLoading(false);
        } catch (error) {
            console.error(error);
            setLoading(false);
        }
    }, [userEmail]);

    useEffect(() => { fetchTickets(); }, [fetchTickets]);

    const updateTicket = async (id, payload) => {
        try {
            const params = new URLSearchParams({
                query: 'zoho_jobs_update',
                id,
                ...payload
            });

            await fetch(`${API}?${params.toString()}`);


            setAllTickets(prev =>
                prev.map(ticket =>
                    String(ticket.id) === String(id)
                        ? { ...ticket, ...payload }
                        : ticket
                )
            );

        } catch (error) {
            console.error("Update failed:", error);
        }
    };


    const handleBulkAction = async (status) => {
        const ids = Array.from(selectedIds);
        if (status === 'approved') {
            const invalid = ids.some(id => {
                const t = activeTickets.find(x => String(x.id) === id);
                return !t?.Account_No || t.Account_No.trim() === '';
            });
            if (invalid) return alert("Cannot approve tickets with missing C0");
        }

        const promises = ids.map(id => {
            const t = allTickets.find(x => String(x.id) === id);
            return updateTicket(id, { Status: status, Account_No: t.Account_No });
        });

        await Promise.allSettled(promises);
        setSelectedIds(new Set());
    };

    const activeTickets = useMemo(() => {
        return allTickets.filter(t => {
            const status = (t.Status || '').toLowerCase();
            const isActive = status === 'pending' || status === 'skipped';
            if (!isActive) return false;

            const hasC0 = t.Account_No && t.Account_No.trim() !== '';
            if (c0Filter === 'with' && !hasC0) return false;
            if (c0Filter === 'without' && hasC0) return false;

            if (c0Filter === 'skipped' && status !== 'skipped') return false;

            return Object.values(t).some(v => String(v || '').toLowerCase().includes(searchTerm.toLowerCase()));
        }).sort((a, b) => {
            const aV = a[sortConfig.key] || '';
            const bV = b[sortConfig.key] || '';
            return sortConfig.direction === 'asc' ? (aV > bV ? 1 : -1) : (aV < bV ? 1 : -1);
        });
    }, [allTickets, searchTerm, c0Filter, sortConfig]);

    const archivedTickets = useMemo(() => {
        return allTickets.filter(t => {
            const status = (t.Status || '').toLowerCase();
            const isArchived = status !== 'pending' && status !== 'skipped';
            if (!isArchived) return false;
            return Object.values(t).some(v => String(v || '').toLowerCase().includes(archivedSearchTerm.toLowerCase()));
        }).sort((a, b) => new Date(b.Datetime || 0) - new Date(a.Datetime || 0));
    }, [allTickets, archivedSearchTerm]);

    const paginate = (data, page, size) => data.slice((page - 1) * size, page * size);

    return (
        <div className="p-6 max-w-full bg-gray-50 min-h-screen font-sans">

            <div className="mb-6">

                {}
                <div className="flex gap-2 mb-4">
                    {['all', 'pending', 'inserted'].map(mode => (
                        <button
                            key={mode}
                            onClick={() => setStatsMode(mode)}
                            className={`px-4 py-2 text-sm rounded-lg transition-all ${
                                statsMode === mode
                                    ? 'bg-blue-600 text-white shadow-md'
                                    : 'bg-white border border-gray-200 hover:bg-gray-100'
                            }`}
                        >
                            {mode === 'all' ? 'All'
                                : mode === 'pending' ? 'Pending'
                                    : 'Inserted'}
                        </button>
                    ))}


                    {TEAM_STATS_ALLOWED.includes(email) && (
                        <button
                            onClick={() => setShowTeamStats(true)}
                            className="ml-auto px-4 py-2 text-sm rounded-lg bg-gray-800 text-white hover:bg-gray-700 transition-all"
                        >
                            Team Stats
                        </button>
                    )}
                </div>

                {}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">

                    <StatCard
                        title="Total Tickets"
                        value={retailerStats.totalTickets}
                        onClick={() =>
                            setActiveBreakdown(
                                activeBreakdown === 'timeline' ? null : 'timeline'
                            )
                        }
                        active={activeBreakdown === 'timeline'}
                    />

                    <StatCard
                        title="Unique Retailers"
                        value={retailerStats.uniqueRetailers}
                        onClick={() =>
                            setActiveBreakdown(
                                activeBreakdown === 'retailers' ? null : 'retailers'
                            )
                        }
                        active={activeBreakdown === 'retailers'}
                    />

                    <StatCard
                        title="Top Marketing"
                        value={marketingStats[0]?.key || '—'}
                        subtitle={`${marketingStats[0]?.count || 0} uses`}
                        onClick={() =>
                            setActiveBreakdown(
                                activeBreakdown === 'marketing' ? null : 'marketing'
                            )
                        }
                        active={activeBreakdown === 'marketing'}
                    />

                </div>
                {}
                {activeBreakdown === 'marketing' && (
                    <div className="mt-6 bg-white p-4 rounded-lg shadow-sm border border-gray-200 animate-fadeIn">
                        <h3 className="font-semibold text-gray-700 mb-3">
                            Marketing Breakdown
                        </h3>

                        <div className="space-y-2 max-h-64 overflow-y-auto">
                            {marketingStats.map(m => (
                                <div key={m.key}>
                                    <div className="flex justify-between text-xs mb-1">
                                        <span>{m.key}</span>
                                        <span>{m.count} ({m.percent}%)</span>
                                    </div>
                                    <div className="h-2 bg-gray-100 rounded-full">
                                        <div
                                            className="h-2 bg-blue-500 rounded-full transition-all"
                                            style={{ width: `${m.percent}%` }}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}



                {activeBreakdown === 'retailers' && (
                    <div className="mt-6 bg-white p-4 rounded-lg shadow-sm border border-gray-200 animate-fadeIn">
                        <div className="flex justify-between items-center mb-3">
                            <h3 className="font-semibold text-gray-700">
                                Retailer Breakdown
                            </h3>
                            <span className="text-xs text-gray-400">
                {retailerBreakdown.length} retailers
            </span>
                        </div>

                        <div className="space-y-3 max-h-80 overflow-y-auto">
                            {retailerBreakdown.map(r => (
                                <div key={r.account}>
                                    <div className="flex justify-between text-xs mb-1">
                                        <div className="font-mono">{r.account}</div>
                                        <div>
                                            {r.total} tickets ({r.percent}%)
                                        </div>
                                    </div>

                                    <div className="h-2 bg-gray-100 rounded-full">
                                        <div
                                            className="h-2 bg-indigo-500 rounded-full transition-all"
                                            style={{ width: `${r.percent}%` }}
                                        />
                                    </div>

                                    <div className="flex gap-3 text-[10px] text-gray-500 mt-1">
                        <span className="text-yellow-600">
                            Pending: {r.pending}
                        </span>
                                        <span className="text-green-600">
                            Inserted: {r.inserted}
                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}


                {activeBreakdown === 'timeline' && (
                    <div className="mt-6 bg-white p-4 rounded-lg shadow-sm border border-gray-200 animate-fadeIn">
                        <h3 className="font-semibold text-gray-700 mb-3">
                            Last 7 Days Activity
                        </h3>

                        <div className="space-y-3">
                            {timelineStats.data.map(d => (
                                <div key={d.date}>
                                    <div className="flex justify-between text-xs mb-1">
                        <span>
                            {new Date(d.date).toLocaleDateString()}
                        </span>
                                        <span>{d.count} tickets</span>
                                    </div>

                                    <div className="h-2 bg-gray-100 rounded-full">
                                        <div
                                            className="h-2 bg-emerald-500 rounded-full transition-all"
                                            style={{
                                                width: `${(d.count / timelineStats.max) * 100}%`
                                            }}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

            </div>



            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="p-4 border-b border-gray-200 flex flex-wrap items-center justify-between gap-4">
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => handleBulkAction('approved')}
                            disabled={selectedIds.size === 0}
                            className="px-4 py-2 bg-blue-600 text-white rounded-md disabled:bg-gray-300 transition-colors flex items-center gap-2"
                        >
                            <Check size={16} /> Approve Selected
                        </button>
                        <button
                            onClick={() => handleBulkAction('rejected')}
                            disabled={selectedIds.size === 0}
                            className="px-4 py-2 bg-red-600 text-white rounded-md disabled:bg-gray-300 transition-colors flex items-center gap-2"
                        >
                            <X size={16} /> Reject Selected
                        </button>
                        <span className="text-sm text-gray-500 ml-2 font-medium">{selectedIds.size} selected</span>
                    </div>

                    <div className="flex items-center gap-3">
                        <div className="flex bg-gray-100 p-1 rounded-lg">
                            {['all', 'with', 'without', 'skipped'].map(f => (
                                <button
                                    key={f}
                                    onClick={() => setC0Filter(f)}
                                    className={`px-3 py-1 text-xs rounded-md transition-all ${c0Filter === f ? 'bg-white shadow-sm text-blue-600' : 'text-gray-600'}`}
                                >
                                    {f === 'all' ? 'All' : f === 'with' ? 'With C0' : f === 'without' ? 'Missing C0' : 'Skipped'} {}
                                </button>
                            ))}
                        </div>
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                            <input
                                type="text"
                                placeholder="Search active tickets..."
                                className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 outline-none w-64"
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead className="bg-gray-50 text-gray-600 text-sm uppercase">
                        <tr>
                            <th className="p-4 border-b w-10">
                                <input
                                    type="checkbox"
                                    checked={activeTickets.length > 0 && selectedIds.size === activeTickets.length}
                                    onChange={e => {
                                        if (e.target.checked) setSelectedIds(new Set(activeTickets.map(t => String(t.id))));
                                        else setSelectedIds(new Set());
                                    }}
                                />
                            </th>
                            <SortableHeader label="Date" k="Scheduled_Date" config={sortConfig} setConfig={setSortConfig} />
                            <th className="p-4 border-b">Marketing</th>
                            <th className="p-4 border-b">Marketing Notes</th>
                            <th className="p-4 border-b">Assistance</th>
                            <th className="p-4 border-b">Assistance Notes</th>
                            <th className="p-4 border-b">Account No</th>
                            <th className="p-4 border-b">Email</th>
                            <th className="p-4 border-b">Status</th>
                            <th className="p-4 border-b text-right">Actions</th>
                        </tr>
                        </thead>
                        <tbody className="text-sm divide-y divide-gray-200">
                        {paginate(activeTickets, currentPage, entriesPerPage).map(t => (
                            <TicketRow
                                key={t.id}
                                ticket={t}
                                isSelected={selectedIds.has(String(t.id))}
                                toggleSelect={id => {
                                    const next = new Set(selectedIds);
                                    if (next.has(id)) next.delete(id); else next.add(id);
                                    setSelectedIds(next);
                                }}
                                fetchTickets={fetchTickets}
                                onUpdate={updateTicket}
                                onViewThread={id => setModalThread(id)}
                            />
                        ))}
                        </tbody>
                    </table>
                </div>

                <Pagination
                    total={activeTickets.length}
                    current={currentPage}
                    size={entriesPerPage}
                    setPage={setCurrentPage}
                    setSize={setEntriesPerPage}
                />
            </div>

            <div className="mt-8">
                <button
                    onClick={() => setShowArchived(!showArchived)}
                    className="flex items-center gap-2 px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-md transition-colors"
                >
                    <Archive size={18} /> {showArchived ? 'Hide Decided Tickets' : 'Show Decided Tickets'}
                </button>

                {showArchived && (
                    <div className="mt-4 bg-white rounded-lg shadow-sm border border-gray-200">
                        <div className="p-4 border-b flex justify-between items-center">
                            <h3 className="font-bold text-gray-700">Archived History</h3>
                            <input
                                type="text"
                                placeholder="Search archive..."
                                className="px-4 py-2 border border-gray-300 rounded-md w-64"
                                value={archivedSearchTerm}
                                onChange={e => setArchivedSearchTerm(e.target.value)}
                            />
                        </div>
                        <table className="w-full text-left text-sm">
                            <thead className="bg-gray-50">
                            <tr>
                                <th className="p-4 border-b">Date</th>
                                <th className="p-4 border-b">Marketing</th>
                                <th className="p-4 border-b">Account No</th>
                                <th className="p-4 border-b">Status</th>
                            </tr>
                            </thead>
                            <tbody>
                            {paginate(archivedTickets, archivedPage, 10).map(t => (
                                <tr key={t.id} className="border-b opacity-75 grayscale hover:grayscale-0 transition-all">
                                    <td className="p-4">{t.Datetime || t.Scheduled_Date}</td>
                                    <td className="p-4">{Array.isArray(t.Marketing) ? t.Marketing.join(', ') : t.Marketing}</td>
                                    <td className="p-4 font-mono">{t.Account_No}</td>
                                    <td className="p-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-bold ${t.Status === 'approved' ? 'bg-blue-100 text-blue-700' : 'bg-red-100 text-red-700'}`}>
                          {t.Status}
                        </span>
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                        <Pagination
                            total={archivedTickets.length}
                            current={archivedPage}
                            size={10}
                            setPage={setArchivedPage}
                        />
                    </div>
                )}
            </div>

            {modalThread && <ThreadModal id={modalThread} onClose={() => setModalThread(null)} />}

            {showTeamStats && <TeamStatsModal onClose={() => setShowTeamStats(false)}/>}
        </div>
    );
};

const SortableHeader = ({ label, k, config, setConfig }) => {
    const active = config.key === k;
    return (
        <th
            className="p-4 border-b cursor-pointer hover:bg-gray-100 transition-colors group"
            onClick={() => setConfig({ key: k, direction: active && config.direction === 'asc' ? 'desc' : 'asc' })}
        >
            <div className="flex items-center gap-1">
                {label}
                <div className={`transition-opacity ${active ? 'opacity-100' : 'opacity-0 group-hover:opacity-50'}`}>
                    {active && config.direction === 'desc' ? <ChevronDown size={14}/> : <ChevronUp size={14}/>}
                </div>
            </div>
        </th>
    );
};



const TicketRow = ({ ticket, isSelected, toggleSelect, onUpdate, onViewThread, fetchTickets }) => {
    const [isEditingAcct, setIsEditingAcct] = useState(false);
    const [isEditingMarketing, setIsEditingMarketing] = useState(false);
    const [isEditingMarketingNotes, setIsEditingMarketingNotes] = useState(false);
    const [isEditingAssistance, setIsEditingAssistance] = useState(false);
    const [isEditingAssistanceNotes, setIsEditingAssistanceNotes] = useState(false);
    const status = (ticket.Status || '').toLowerCase();

    return (
        <tr className="hover:bg-blue-50/30 transition-colors group">
            <td className="p-4 border-b">
                <input
                    type="checkbox"
                    checked={isSelected}
                    disabled={status === 'pending' && (!ticket.Account_No || ticket.Account_No.trim() === '')}
                    onChange={() => toggleSelect(String(ticket.id))}
                />
            </td>
            <td className="p-4 border-b text-gray-600">{ticket.Scheduled_Date}</td>
            <td className="p-4 border-b" onDoubleClick={() => setIsEditingMarketing(true)}>
                {isEditingMarketing ? (
                    <MarketingEditor
                        value={ticket.Marketing}
                        onSave={val => { onUpdate(ticket.id, { Marketing: JSON.stringify(val) }); setIsEditingMarketing(false); }}
                        onClose={() => setIsEditingMarketing(false)}
                    />
                ) : (
                    <span className="cursor-pointer border-b border-dashed border-gray-300">{Array.isArray(ticket.Marketing) ? ticket.Marketing.join(', ') : ticket.Marketing}</span>
                )}
            </td>
            <td
                className="p-4 border-b whitespace-pre-wrap break-words"
                onDoubleClick={() => setIsEditingMarketingNotes(true)}
            >
                {isEditingMarketingNotes ? (
                    <InlineTextarea
                        initial={ticket.Marketing_Notes || ''}
                        onSave={(val) => {
                            onUpdate(ticket.id, { Marketing_Notes: val });
                            setIsEditingMarketingNotes(false);
                        }}
                        onCancel={() => setIsEditingMarketingNotes(false)}
                    />
                ) : (
                    <span className="cursor-pointer border-b border-dashed border-gray-300">
      {ticket.Marketing_Notes || ''}
    </span>
                )}
            </td>
            <td
                className="p-4 border-b"
                onDoubleClick={() => setIsEditingAssistance(true)}
            >
                {isEditingAssistance ? (
                    <InlineSelect
                        initial={ticket.Other_Assistance.toLowerCase() === 'yes' ? 1 : 0}
                        options={[
                            { label: 'YES', value: 1 },
                            { label: 'NO', value: 0 }
                        ]}
                        onSave={(val) => {
                            onUpdate(ticket.id, { Other_Assistance: val });
                            setIsEditingAssistance(false);
                        }}
                        onCancel={() => setIsEditingAssistance(false)}
                    />
                ) : (
                    <span className="cursor-pointer">
      <span
          className={`px-2 py-0.5 rounded-full text-xs font-bold ${
              ticket.Other_Assistance ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
          }`}
      >
        {ticket.Other_Assistance ? 'YES' : 'NO'}
      </span>
    </span>
                )}
            </td>

            <td
                className="p-4 border-b whitespace-pre-wrap break-words"
                onDoubleClick={() => setIsEditingAssistanceNotes(true)}
            >
                {isEditingAssistanceNotes ? (
                    <InlineTextarea
                        initial={ticket.Other_Assistance_Notes || ''}
                        onSave={(val) => {
                            onUpdate(ticket.id, { Other_Assistance_Notes: val });
                            setIsEditingAssistanceNotes(false);
                        }}
                        onCancel={() => setIsEditingAssistanceNotes(false)}
                    />
                ) : (
                    <span className="cursor-pointer border-b border-dashed border-gray-300">
      {ticket.Other_Assistance_Notes || ''}
    </span>
                )}
            </td>

            <td className="p-4 border-b font-mono" onDoubleClick={() => setIsEditingAcct(true)}>
                {isEditingAcct || (!ticket.Account_No) ? (
                    <AccountSearch
                        onSelect={async val => {
                            await onUpdate(ticket.id, { Account_No: val });
                            setIsEditingAcct(false);
                        }}
                        initial={ticket.Account_No}
                        ogticket={ticket}
                        fetchTickets={fetchTickets}
                    />
                ) : (
                    <span className="cursor-pointer">{ticket.Account_No}</span>
                )}
            </td>

            <td className="p-4 border-b text-gray-500">{ticket.GabrielEmployeeEmail}</td>
            <td className="p-4 border-b">
                {status === 'skipped' ? (
                    <div className="flex flex-col">
                        <span className="text-red-600 font-bold uppercase text-[10px]">Skipped</span>
                        <span className="text-xs text-gray-500">{ticket.reason}</span>
                    </div>
                ) : <span className="text-gray-400 italic">Pending</span>}
            </td>
            <td className="p-4 border-b text-right space-x-1">
                <div className="flex flex-col gap-1 items-end">
                    <div className="flex gap-1">
                        <button
                            onClick={() => onUpdate(ticket.id, { Status: 'approved', Account_No: ticket.Account_No })}
                            disabled={!ticket.Account_No}
                            className={`p-1.5 rounded transition-colors ${status === 'approved' ? 'bg-blue-600 text-white' : 'bg-blue-50 text-blue-600 hover:bg-blue-100 disabled:opacity-50'}`}
                        >
                            <Check size={14} />
                        </button>
                        <button
                            onClick={() => onUpdate(ticket.id, { Status: 'rejected', Account_No: ticket.Account_No })}
                            className={`p-1.5 rounded transition-colors ${status === 'rejected' ? 'bg-red-600 text-white' : 'bg-red-50 text-red-600 hover:bg-red-100'}`}
                        >
                            <X size={14} />
                        </button>
                    </div>
                    <button
                        onClick={() => onViewThread(ticket.conversationId || ticket.messageId)}
                        className="text-[10px] text-teal-700 bg-teal-50 px-2 py-1 rounded hover:bg-teal-100 flex items-center gap-1"
                    >
                        <MessageSquare size={10} /> View Thread
                    </button>
                    <button
                        onClick={() => onUpdate(ticket.id, { Report: 1 })}
                        className="text-[10px] text-yellow-700 bg-yellow-50 px-2 py-1 rounded hover:bg-yellow-100 flex items-center gap-1"
                    >
                        <AlertTriangle size={10} /> Report
                    </button>
                </div>
            </td>
        </tr>
    );
};



const InlineTextarea = ({ initial, onSave, onCancel }) => {
    const [val, setVal] = useState(initial);

    return (
        <div className="flex flex-col gap-2">
      <textarea
          autoFocus
          value={val}
          onChange={(e) => setVal(e.target.value)}
          rows={3}
          className="w-full border border-gray-300 rounded px-2 py-1 text-sm outline-none focus:ring-2 focus:ring-blue-500"
      />
            <div className="flex gap-2 justify-end">
                <button
                    className="px-2 py-1 text-xs rounded bg-gray-100 hover:bg-gray-200"
                    onClick={onCancel}
                >
                    Cancel
                </button>
                <button
                    className="px-2 py-1 text-xs rounded bg-blue-600 text-white hover:bg-blue-700"
                    onClick={() => onSave(val)}
                >
                    Save
                </button>
            </div>
        </div>
    );
};

const InlineSelect = ({ initial, options, onSave, onCancel }) => {
    const [val, setVal] = useState(initial);

    return (
        <div className="flex items-center gap-2">
            <select
                autoFocus
                value={val}
                onChange={(e) => setVal(e.target.value)}
                className="border border-gray-300 rounded px-2 py-1 text-sm outline-none focus:ring-2 focus:ring-blue-500"
            >
                {options.map(opt => (
                    <option key={opt.label} value={opt.value}>{opt.label}</option>
                ))}
            </select>

            <button
                className="px-2 py-1 text-xs rounded bg-gray-100 hover:bg-gray-200"
                onClick={onCancel}
            >
                Cancel
            </button>
            <button
                className="px-2 py-1 text-xs rounded bg-blue-600 text-white hover:bg-blue-700"
                onClick={() => onSave(val)}
            >
                Save
            </button>
        </div>
    );
};

const AccountSearch = ({ onSelect, initial = '', ogticket, fetchTickets }) => {
    const [val, setVal] = useState(initial);
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (val.length < 2) return setResults([]);
        const timer = setTimeout(async () => {
            setLoading(true);
            const res = await fetch(`${API}?query=zoho_search&q=${val}`);
            const data = await res.json();
            setResults(data.data || []);
            setLoading(false);
        }, 500);
        return () => clearTimeout(timer);
    }, [val]);

    const GENERIC_DOMAINS = [
        'gmail.com', 'googlemail.com', 'google.com',
        'outlook.com', 'hotmail.com', 'hotmail.co.uk', 'live.com',
        'live.co.uk', 'msn.com', 'microsoft.com',
        'yahoo.com', 'yahoo.co.uk', 'yahoo.fr', 'yahoo.es', 'ymail.com',
        'icloud.com', 'me.com', 'mac.com',
        'aol.com', 'aim.com', 'verizon.net',
        'protonmail.com', 'proton.me', 'zoho.com', 'zohomail.com',
        'dropbox.com', 'box.com', 'docusign.com',
        'comcast.net', 'att.net', 'sbcglobal.net', 'bellsouth.net',
        'cox.net', 'charter.net', 'earthlink.net',
        'mail.com', 'email.com', 'inbox.com', 'gmx.com', 'gmx.net'
    ];

    const handleSelectAccount = async (accountNo, row) => {
        try {
            await onSelect(accountNo);

            const isRetailerDomain = ogticket.retailerDomainName &&
                ogticket.retailerDomainName.trim() !== '' &&
                !GENERIC_DOMAINS.includes(ogticket.retailerDomainName.toLowerCase());

            if (isRetailerDomain) {
                await fetch(
                    `${API}?query=bulk_update_retailer_pending` +
                    `&email=${encodeURIComponent(ogticket.GabrielEmployeeEmail)}` +
                    `&retailerDomain=${encodeURIComponent(ogticket.retailerDomainName)}` +
                    `&accountNo=${encodeURIComponent(accountNo)}`
                );
                await fetch(
                    `${API}?query=insert_or_update_retailer` +
                    `&retailerDomain=${encodeURIComponent(ogticket.retailerDomainName)}` +
                    `&retailerC0=${encodeURIComponent(accountNo)}`
                );
            }

            await fetchTickets();
        } catch (err) {
            console.error("Bulk update flow failed:", err);
        }
    };


    return (
        <div className="relative w-full min-w-[150px]">
            <input
                autoFocus
                className="w-full px-2 py-1 border rounded shadow-inner outline-none focus:ring-1 focus:ring-blue-500"
                value={val}
                onChange={e => setVal(e.target.value)}
                placeholder="C0 Number..."
            />
            {results.length > 0 && (
                <div className="absolute z-50 mt-1 w-64 bg-white border rounded shadow-xl max-h-48 overflow-y-auto">
                    {results.map(r => (
                        <div
                            key={r.Customer_No}
                            onClick={() => handleSelectAccount(r.Customer_No, r)}
                            className="p-2 hover:bg-blue-50 cursor-pointer text-xs border-b last:border-0"
                        >
                            <div className="font-bold">{r.Account_Name}</div>
                            <div className="text-gray-500">{r.Customer_No}</div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

const MarketingEditor = ({ value, onSave, onClose }) => {
    const current = useMemo(() => {
        if (Array.isArray(value)) return value;
        try { return JSON.parse(value) || []; } catch(e) { return value ? value.split(',').map(s => s.trim()) : []; }
    }, [value]);

    const [selected, setSelected] = useState(current);

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/20" onClick={onClose}>
            <div className="bg-white p-4 rounded-lg shadow-2xl w-96 max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
                <h4 className="font-bold mb-3 border-b pb-2">Select Marketing Types</h4>
                <div className="overflow-y-auto flex-1 space-y-1">
                    {MARKETING_OPTIONS.map(opt => (
                        <label key={opt} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer text-xs">
                            <input
                                type="checkbox"
                                checked={selected.includes(opt)}
                                onChange={() => {
                                    const next = selected.includes(opt) ? selected.filter(s => s !== opt) : [...selected, opt];
                                    setSelected(next);
                                }}
                            />
                            {opt}
                        </label>
                    ))}
                </div>
                <div className="mt-4 flex gap-2">
                    <button onClick={() => onSave(selected)} className="flex-1 py-2 bg-blue-600 text-white rounded font-bold text-sm">Save Changes</button>
                    <button onClick={onClose} className="flex-1 py-2 bg-gray-100 rounded text-sm">Cancel</button>
                </div>
            </div>
        </div>
    );
};

const Pagination = ({ total, current, size, setPage, setSize }) => {
    const totalPages = Math.ceil(total / size);
    if (totalPages <= 1) return null;

    return (
        <div className="p-4 border-t bg-gray-50 flex items-center justify-between">
            <div className="text-sm text-gray-500">
                Showing {(current - 1) * size + 1} to {Math.min(current * size, total)} of {total}
            </div>
            <div className="flex items-center gap-1">
                <button
                    disabled={current === 1}
                    onClick={() => setPage(current - 1)}
                    className="p-1 rounded hover:bg-white disabled:opacity-30"
                >
                    <ChevronLeft size={20} />
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter(p => Math.abs(p - current) < 3 || p === 1 || p === totalPages)
                    .map((p, i, arr) => (
                        <React.Fragment key={p}>
                            {i > 0 && arr[i-1] !== p - 1 && <span className="px-2">...</span>}
                            <button
                                onClick={() => setPage(p)}
                                className={`w-8 h-8 rounded text-sm ${current === p ? 'bg-blue-600 text-white' : 'hover:bg-white'}`}
                            >
                                {p}
                            </button>
                        </React.Fragment>
                    ))}
                <button
                    disabled={current === totalPages}
                    onClick={() => setPage(current + 1)}
                    className="p-1 rounded hover:bg-white disabled:opacity-30"
                >
                    <ChevronRight size={20} />
                </button>
            </div>
        </div>
    );
};


export default ZohoTicketManager;